import { useEffect, useState, useCallback } from 'react';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { CustomerType } from '../../../../../generated/ApiClient';

export interface IdentityCheckCustomer {
  customerId: number;
  label: string;
  displayName: string;
  customerType: CustomerType;
  isMainCustomer: boolean;
  identityCheckId?: string;
  ksvSearchDone: boolean;
  ksvId?: string;
}

async function loadCustomer(
  dealId: number,
  customerId: number,
  label: string,
  isMain: boolean,
  fallbackName: string
): Promise<IdentityCheckCustomer> {
  const typeRes = await RestHttpClient.getCustomerType(customerId);
  const customerType = typeRes.data;

  if (customerType === CustomerType.Commercial) {
    const r = await RestHttpClient.getCommercialDealCustomer(dealId, customerId);
    const d = r.data;
    return {
      customerId,
      label,
      displayName: d?.companyName ?? fallbackName,
      customerType,
      isMainCustomer: isMain,
      identityCheckId: d?.identityCheckId ?? undefined,
      ksvSearchDone: d?.ksvSearchDone ?? false,
      ksvId: d?.ksvId ?? undefined,
    };
  }

  const r = await RestHttpClient.getPrivateDealCustomer(dealId, customerId);
  const d = r.data;
  return {
    customerId,
    label,
    displayName:
      [d?.firstName, d?.lastName].filter(Boolean).join(' ') || fallbackName,
    customerType,
    isMainCustomer: isMain,
    identityCheckId: d?.identityCheckId ?? undefined,
    ksvSearchDone: d?.ksvSearchDone ?? false,
    ksvId: d?.ksvId ?? undefined,
  };
}

export function useIdentityCheckCustomers(dealId: number, mainCustomerId: number, enabled = true) {
  const [customers, setCustomers] = useState<IdentityCheckCustomer[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled || !dealId || !mainCustomerId) return;
    setLoading(true);
    try {
      const coAppResult = await RestHttpClient.getDealCoApplicantOverview(dealId);
      const coApplicants = coAppResult.data ?? [];

      const mainPromise = loadCustomer(dealId, mainCustomerId, 'Kunde', true, String(mainCustomerId));
      const coAppPromises = coApplicants.map((c, i) =>
        loadCustomer(
          dealId,
          c.customerId,
          `Mitantragsteller ${i + 1}`,
          false,
          `${c.firstName} ${c.lastName}`.trim()
        )
      );

      const all = await Promise.all([mainPromise, ...coAppPromises]);
      setCustomers(all);
    } finally {
      setLoading(false);
    }
  }, [dealId, mainCustomerId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { customers, loading, refetch: load };
}
